<template>
  <div class="role-management">
    <div class="page-title">角色权限管理</div>
    
    <!-- 搜索筛选区域 -->
    <div class="filter-section">
      <div class="filter-row">
        <el-input
          placeholder="搜索角色名称"
          v-model="searchKeyword"
          class="filter-input"
          @keyup.enter.native="handleSearch">
          <i slot="prefix" class="el-input__icon el-icon-search"></i>
        </el-input>
        <el-button type="primary" plain @click="handleSearch">查询</el-button>
        <el-button @click="resetSearch">重置</el-button>
        <div class="spacer"></div>
        <el-button type="primary" icon="el-icon-plus" @click="handleAddRole">新增角色</el-button>
      </div>
    </div>

    <el-table
      :data="roleData"
      border
      style="width: 100%"
      v-loading="loading">
      <el-table-column prop="roleName" label="角色名称" width="180"></el-table-column>
      <el-table-column prop="webPermissions" label="权限" min-width="280">
        <template slot-scope="scope">
          {{ scope.row.webPermissions || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="creator" label="创建人" width="120" align="center"></el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="180" align="center"></el-table-column>
      <el-table-column prop="lastModifier" label="最后修改人" width="120" align="center"></el-table-column>
      <el-table-column prop="lastModifiedTime" label="最后修改时间" width="180" align="center"></el-table-column>
      <el-table-column label="操作" width="150" align="center">
        <template slot-scope="scope">
          <el-button type="text" @click="handleEditRole(scope.row)">编辑</el-button>
          <el-button type="text" @click="handleDeleteRole(scope.row)" class="delete-btn">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total">
      </el-pagination>
    </div>

    <!-- 新增/编辑角色弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false">
      
      <div class="dialog-content">
        <el-form :model="roleForm" :rules="rules" ref="roleForm" label-width="100px">
          <el-form-item label="角色名称" prop="roleName">
            <el-input v-model="roleForm.roleName" placeholder="请输入角色名称"></el-input>
          </el-form-item>
          
          <el-form-item label="功能权限" prop="permissionList">
            <div class="permission-tree-container">
              <el-tree
                ref="webPermissionTree"
                :data="resourceTree.web"
                show-checkbox
                node-key="id"
                default-expand-all
                :check-strictly="defaultCheckStrictly"
                :props="{
                  children: 'children',
                  label: 'name'
                }"
                @check="handlePermissionCheck">
              </el-tree>
            </div>
          </el-form-item>
        </el-form>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitRoleForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { searchRoles, addRole, updateRole, deleteRole, getResourceTree, getRoleById } from '@/api/role'
import request from '@/utils/request'

export default {
  name: 'RoleManagement',
  data() {
    return {
      searchKeyword: '',
      roleData: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      
      // 弹窗相关数据
      dialogVisible: false,
      dialogTitle: '新增角色',
      roleForm: {
        roleName: '',
        permissionList: []
      },
      editingRoleId: null,
      
      // 表单验证规则
      rules: {
        roleName: [
          { required: true, message: '请输入角色名称', trigger: 'blur' },
          { min: 2, max: 20, message: '长度在 2 到 20 个字符', trigger: 'blur' }
        ],
        permissionList: [
          { type: 'array', required: true, message: '请至少选择一个权限', trigger: 'change' }
        ]
      },
      resourceTree: {
        web: []
      },
      allResources: [],
      defaultCheckStrictly: true
    };
  },
  created() {
    this.fetchRoleList();
    this.fetchResourceTree();
  },
  methods: {
    // 获取资源树
    fetchResourceTree() {
      this.loading = true;
      getResourceTree()
        .then(response => {
          console.log('获取资源树响应:', response);
          if (response && response.code === 200 && response.data) {
            this.allResources = response.data || [];
            
            // 根据treeCode排序
            this.allResources.sort((a, b) => {
              if (a.treeCode && b.treeCode) {
                return a.treeCode.localeCompare(b.treeCode);
              }
              return 0;
            });
            
            // 构建Web端的树形结构
            this.resourceTree = this.buildResourceTree(this.allResources);
            console.log('构建的资源树:', this.resourceTree);
          } else {
            this.$message.error((response && response.msg) || '获取资源树失败');
          }
        })
        .catch(error => {
          console.error('获取资源树失败:', error);
          this.$message.error('获取资源树失败，请刷新页面重试');
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 构建资源树
    buildResourceTree(resources) {
      const webResources = resources.filter(r => r.terminalType === 1 || !r.terminalType);
      
      return {
        web: this.getResourcesWithTreeCode(webResources)
      };
    },
    
    // 根据treeCode构建树形结构
    getResourcesWithTreeCode(resources) {
      const result = [];
      
      // 先找出所有根节点
      const rootNodes = resources.filter(r => (!r.parentId || r.parentId === 0) && (!r.treeCode || r.treeCode.length <= 3));
      
      // 为了防止重复，记录已处理过的资源ID
      const processedIds = new Set();
      
      // 对于每个根节点，递归找出其子节点
      rootNodes.forEach(root => {
        processedIds.add(root.id); // 记录已处理的根节点
        
        const node = {
          ...root,
          label: root.name,
          value: root.id,
          children: this.getChildrenByParentId(resources, root.id, root.treeCode, processedIds)
        };
        result.push(node);
      });
      
      return result;
    },
    
    // 根据父节点ID和treeCode递归获取子节点
    getChildrenByParentId(resources, parentId, parentTreeCode, processedIds) {
      // 找出直接子节点（通过parentId）
      const directChildren = resources.filter(r => 
        r.parentId === parentId && !processedIds.has(r.id)
      );
      
      // 找出通过treeCode关联的间接子节点（但排除已经是直接子节点的那些）
      const indirectChildren = parentTreeCode ? 
        resources.filter(r => 
          r.treeCode && 
          r.treeCode.startsWith(parentTreeCode) && 
          r.treeCode !== parentTreeCode && 
          r.parentId !== parentId && 
          !processedIds.has(r.id)
        ) : [];
      
      // 合并所有子节点
      const allChildren = [...directChildren, ...indirectChildren];
      
      if (allChildren.length === 0) {
        return undefined;
      }
      
      // 记录所有子节点的ID，避免重复
      allChildren.forEach(child => processedIds.add(child.id));
      
      return allChildren.map(child => ({
        ...child,
        label: child.name,
        value: child.id,
        children: this.getChildrenByParentId(resources, child.id, child.treeCode, processedIds)
      }));
    },
    
    // 获取角色列表
    fetchRoleList() {
      this.loading = true;
      
      // 准备查询参数
      const params = {};
      if (this.searchKeyword && this.searchKeyword.trim() !== '') {
        params.name = this.searchKeyword.trim();
      }
      
      // 添加分页参数
      params.pageNum = this.currentPage;
      params.pageSize = this.pageSize;
      
      console.log('发送分页查询角色请求，参数:', JSON.stringify(params));
      
      searchRoles(params)
        .then(response => {
          console.log('分页查询角色响应:', response);
          if (response && response.code === 200) {
            // 检查响应数据结构
            let dataList = [];
            
            if (response.data && Array.isArray(response.data.list)) {
              // 新的响应结构
              dataList = response.data.list || [];
              this.total = response.data.total || 0;
            } else {
              // 旧的响应结构
              dataList = response.data || [];
              this.total = response.total || dataList.length;
            }
            
            this.roleData = this.processRoleData(dataList);
          } else {
            this.$message.error((response && response.msg) || '获取角色列表失败');
            this.roleData = [];
            this.total = 0;
          }
        })
        .catch(error => {
          console.error('获取角色列表失败:', error);
          // 不在这里显示错误消息，避免重复提示
          this.roleData = [];
          this.total = 0;
        })
        .finally(() => {
        this.loading = false;
        });
    },
    
    // 处理角色数据
    processRoleData(dataList) {
      return dataList.map((item) => {
        // 使用后端返回的权限描述字段
        let webPermissions = item.webPermissionDesc || '';
        
        // 如果后端没有返回权限描述，才尝试从资源中提取
        if (!webPermissions && item.resources && Array.isArray(item.resources)) {
          // 只保留Web端资源
          const webResources = item.resources.filter(r => 
            r.terminalType === 1 || 
            r.name.includes('系统') || 
            r.name.includes('管理') ||
            !r.name.includes('小程序')
          );
          
          if (!webPermissions) {
            webPermissions = webResources.map(r => r.name).join('; ');
            if (webPermissions) webPermissions += ';';
          }
        }
        
        // 如果没有updateUserName但有updateUserId，则使用默认值
        if (!item.updateUserName && item.updateUserId) {
          item.updateUserName = item.updateUserName || '';
        }
        
        // 如果没有创建人信息但有创建者ID，使用默认值
        if (!item.creator && item.createUserId) {
          item.creator = '';
        }
        
        return {
          id: item.id,
          roleName: item.name,
          webPermissions: webPermissions || '-',
          creator: item.creator || item.createUserName || '-', 
          createTime: item.createTime ? this.formatDateTime(item.createTime) : '-',
          lastModifier: item.updateUserName || '-',
          lastModifiedTime: item.updateTime ? this.formatDateTime(item.updateTime) : '-',
          resources: item.resources || [],
          resourceIds: (item.resources || []).map(r => r.id)
        };
      });
    },
    
    // 格式化日期时间
    formatDateTime(timestamp) {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return timestamp; // 如果不是有效日期，则返回原值
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    
    // 搜索
    handleSearch() {
      this.currentPage = 1; // 重置到第一页
      this.fetchRoleList();
    },
    
    // 重置搜索
    resetSearch() {
      this.searchKeyword = '';
      this.currentPage = 1;
      this.fetchRoleList();
    },
    
    // 新增角色
    handleAddRole() {
      this.dialogTitle = '新增角色';
      this.roleForm = {
        roleName: '',
        permissionList: []
      };
      this.editingRoleId = null;
      
      // 新增角色时不使用严格模式，以便支持父子级联选择
      this.defaultCheckStrictly = false;
      this.dialogVisible = true;
      
      // 重置表单验证
      if (this.$refs.roleForm) {
        this.$refs.roleForm.resetFields();
      }
      
      // 清空树选择
      this.$nextTick(() => {
        if (this.$refs.webPermissionTree) {
          this.$refs.webPermissionTree.setCheckedKeys([]);
        }
      });
    },
    
    // 编辑角色
    handleEditRole(row) {
      this.dialogTitle = '编辑角色';
      this.editingRoleId = row.id;
      
      // 设置为严格检查模式，以便只勾选实际权限
      this.defaultCheckStrictly = true;
      
      this.loading = true;
      
      // 使用API获取最新的角色详情
      getRoleById(row.id)
        .then(response => {
          if (response && response.code === 200 && response.data) {
            const roleDetail = response.data;
            console.log('获取角色详情成功:', roleDetail);
            
            // 设置表单数据
            this.roleForm = {
              roleName: roleDetail.name || row.roleName,
              permissionList: []
            };
            
            this.dialogVisible = true;
            
            // 设置已选择的权限
            this.$nextTick(() => {
              // 确保资源树加载完成
              if (!this.allResources.length) {
                this.fetchResourceTree().then(() => {
                  this.setSelectedResources(roleDetail);
                });
              } else {
                this.setSelectedResources(roleDetail);
              }
              
              // 重置表单验证
              if (this.$refs.roleForm) {
                this.$refs.roleForm.clearValidate();
              }
            });
            
          } else {
            this.$message.error((response && response.msg) || '获取角色详情失败');
            
            // 如果获取详情失败，使用列表中的基本信息
            this.roleForm = {
              roleName: row.roleName,
              permissionList: []
            };
            
            this.dialogVisible = true;
            
            this.$nextTick(() => {
              if (!this.allResources.length) {
                this.fetchResourceTree().then(() => {
                  this.setSelectedResources(row);
                });
              } else {
                this.setSelectedResources(row);
              }
              
              if (this.$refs.roleForm) {
                this.$refs.roleForm.clearValidate();
              }
            });
          }
        })
        .catch(error => {
          console.error('获取角色详情失败:', error);
          this.$message.error('获取角色详情失败');
          
          // 如果API调用失败，使用列表中的基本信息
      this.roleForm = {
        roleName: row.roleName,
            permissionList: []
      };
      
      this.dialogVisible = true;
      
      this.$nextTick(() => {
            if (!this.allResources.length) {
              this.fetchResourceTree().then(() => {
                this.setSelectedResources(row);
              });
            } else {
              this.setSelectedResources(row);
            }
            
        if (this.$refs.roleForm) {
          this.$refs.roleForm.clearValidate();
        }
      });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    
    // 设置已选中的资源
    setSelectedResources(roleData) {
      // 确定resourceIds字段
      let resourceIds = [];
      
      if (roleData.resourceIds && Array.isArray(roleData.resourceIds)) {
        // 直接使用resourceIds数组
        resourceIds = roleData.resourceIds;
      } else if (roleData.resources && Array.isArray(roleData.resources)) {
        // 从resources中提取id
        resourceIds = roleData.resources.map(r => r.id);
      }
      
      console.log('设置权限勾选，资源IDs:', resourceIds);
      
      if (resourceIds.length > 0) {
        // 处理树形结构的选择逻辑
        const webResources = this.allResources.filter(r => r.terminalType !== 2);
        
        // 只保留Web端的资源ID
        const webResourceIds = resourceIds.filter(id => {
          const resource = this.allResources.find(r => r.id === id);
          return resource && resource.terminalType !== 2;
        });
        
        console.log('Web资源ID:', webResourceIds);
        
        // 清空之前的选择状态
        if (this.$refs.webPermissionTree) {
          this.$refs.webPermissionTree.setCheckedKeys([]);
        }
        
        // 逐个设置选中状态，避免自动选择所有子节点
        setTimeout(() => {
          if (this.$refs.webPermissionTree) {
            webResourceIds.forEach(id => {
              // 确认节点存在才设置选中状态
              const node = this.$refs.webPermissionTree.getNode(id);
              if (node) {
                this.$refs.webPermissionTree.setChecked(node, true, false);
              }
            });
          }
          
          // 更新表单的permissionList值
          this.handlePermissionCheck();
          
          // 设置完成后，关闭严格检查模式，以便用户手动勾选时可以联动父子节点
          this.defaultCheckStrictly = false;
        }, 100);
      } else {
        // 如果没有资源ID，清空选择
        if (this.$refs.webPermissionTree) {
          this.$refs.webPermissionTree.setCheckedKeys([]);
        }
        
        // 清空后，关闭严格检查模式
        this.defaultCheckStrictly = false;
      }
    },
    
    // 删除角色
    handleDeleteRole(row) {
      this.$confirm('确认删除该角色吗?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.loading = true;
        deleteRole(row.id)
          .then(response => {
            if (response && response.code === 200) {
              this.$message.success('删除成功');
              this.fetchRoleList(); // 重新获取列表
            } else {
              this.$message.error((response && response.msg) || '删除失败');
            }
          })
          .catch(error => {
            console.error('删除失败:', error);
            // 不在这里显示错误消息，避免重复提示
          })
          .finally(() => {
            this.loading = false;
          });
      }).catch(() => {
        // 取消操作
      });
    },
    
    // 处理权限选择
    handlePermissionCheck() {
      // 更新表单的permissionList值，以便表单验证可以正常工作
      const webCheckedKeys = this.$refs.webPermissionTree ? this.$refs.webPermissionTree.getCheckedKeys() : [];
      
      // 使用选中的web权限ID作为表单的permissionList值
      this.roleForm.permissionList = [...webCheckedKeys];
      
      console.log('更新权限选择:', this.roleForm.permissionList);
    },
    
    // 提交角色表单
    submitRoleForm() {
      // 先更新表单的permissionList值
      this.handlePermissionCheck();
      
      this.$refs.roleForm.validate((valid) => {
        if (valid) {
          // 检查是否至少选择了一个权限
          if (this.roleForm.permissionList.length === 0) {
            this.$message.warning('请至少选择一个权限');
            return false;
          }
          
          this.loading = true;
          
          // 获取选中的权限ID
          const webResourceIds = this.$refs.webPermissionTree ? 
            [...this.$refs.webPermissionTree.getCheckedKeys(), ...this.$refs.webPermissionTree.getHalfCheckedKeys()] : [];
          
          // 获取权限描述
          const webPermissions = this.getPermissionDescByIds(webResourceIds);
          
          // 准备提交的数据
          const submitData = {
            name: this.roleForm.roleName.trim(),
            resourceIds: webResourceIds,
            webPermissionDesc: webPermissions || '-',
            appPermissionDesc: '-'
          };
          
          console.log('提交数据:', JSON.stringify(submitData));
          
          if (!this.editingRoleId) {
            // 新增角色
            addRole(submitData)
              .then(response => {
                console.log('新增角色响应:', response);
                if (response && response.code === 200) {
            this.$message.success('新增角色成功');
                  this.dialogVisible = false;
                  this.fetchRoleList(); // 重新获取列表
                } else {
                  this.$message.error((response && response.msg) || '新增角色失败');
                }
              })
              .catch(error => {
                console.error('新增角色失败:', error);
                // 不在这里显示错误消息，避免重复提示
              })
              .finally(() => {
                this.loading = false;
              });
          } else {
            // 编辑角色
            updateRole(this.editingRoleId, submitData)
              .then(response => {
                console.log('编辑角色响应:', response);
                if (response && response.code === 200) {
            this.$message.success('编辑角色成功');
                  this.dialogVisible = false;
                  this.fetchRoleList(); // 重新获取列表
                } else {
                  this.$message.error((response && response.msg) || '编辑角色失败');
                }
              })
              .catch(error => {
                console.error('编辑角色失败:', error);
                // 不在这里显示错误消息，避免重复提示
              })
              .finally(() => {
                this.loading = false;
              });
          }
        } else {
          return false;
        }
      });
    },
    
    // 获取权限描述文本
    getPermissionDescByIds(ids) {
      if (!ids || !ids.length) return '';
      
      // 找出所有匹配的资源
      const resources = this.allResources.filter(r => ids.includes(r.id));
      
      // 提取名称并合并为分号分隔的字符串
      const names = resources.map(r => r.name).filter(Boolean);
      return names.length ? names.join('; ') + ';' : '';
    },
    
    // 分页相关方法
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchRoleList();
    },
    
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchRoleList();
    }
  }
};
</script> 

<style scoped>
.role-management {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.page-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 20px;
  color: #303133;
}

/* 筛选区域样式 */
.filter-section {
  margin-bottom: 20px;
}

.filter-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 10px;
}

.filter-input {
  width: 300px;
}

.spacer {
  flex-grow: 1;
}

/* 删除按钮样式 */
.delete-btn {
  color: #f56c6c;
}

/* 分页容器 */
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 弹窗样式 */
.dialog-content {
  padding: 0 20px;
}

.permission-tree-container {
  height: 300px;
  overflow: auto;
  padding: 15px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #fafafa;
}

/* 树节点样式 */
/deep/ .el-tree-node__content {
  height: 32px;
}

/deep/ .el-tree-node__label {
  font-size: 14px;
}

/* 选中状态样式 */
/deep/ .el-tree-node.is-current > .el-tree-node__content {
  background-color: #f0f7ff;
}

/* 自定义tab样式 */
/deep/ .el-tabs__item {
  height: 40px;
  line-height: 40px;
  font-size: 14px;
}

/deep/ .el-tabs__content {
  padding-top: 10px;
}
</style> 